import { useState, useMemo } from 'react'
import { RefreshCw, Shield, Save } from 'lucide-react'
import { useMenusParaPermissoes, useAtualizarPermissoesMenu } from '../hooks/useMenu'
import { usePapeisDisponiveis } from '../../usuarios/hooks/useUsuario'
import { MenuParaPermissao } from '../services/menu'

function LinhaPermissao({
  menu,
  papeis,
  papelCodigosSelecionados,
  onTogglePapel,
  onSalvar,
  salvando,
}: {
  menu: MenuParaPermissao
  papeis: { codigo: string; nome: string }[]
  papelCodigosSelecionados: number[]
  onTogglePapel: (codigo: number) => void
  onSalvar: () => void
  salvando: boolean
}) {
  const alterado = useMemo(() => {
    const atuais = new Set(menu.papeisComAcesso.map((p) => p.codigo))
    const sel = new Set(papelCodigosSelecionados)
    if (atuais.size !== sel.size) return true
    for (const c of atuais)
      if (!sel.has(c)) return true
    return false
  }, [menu.papeisComAcesso, papelCodigosSelecionados])

  return (
    <tr className="border-b border-gray-200 hover:bg-gray-50/50">
      <td className="px-4 py-3 text-sm font-medium text-gray-900">{menu.titulo}</td>
      <td className="px-4 py-3 text-sm text-gray-600 font-mono">{menu.rota}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{menu.recurso}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-4">
          {papeis.map((papel) => {
            const codigoNum = Number(papel.codigo)
            const checked = papelCodigosSelecionados.includes(codigoNum)
            return (
              <label key={papel.codigo} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onTogglePapel(codigoNum)}
                  className="h-4 w-4 text-brand-500 border-gray-300 rounded focus:ring-brand-400"
                />
                <span className="text-sm text-gray-700">{papel.nome}</span>
              </label>
            )
          })}
        </div>
      </td>
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={onSalvar}
          disabled={!alterado || salvando}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Save className="w-4 h-4" />
          Salvar
        </button>
      </td>
    </tr>
  )
}

function Permissoes() {
  const { data: menus = [], isLoading, refetch } = useMenusParaPermissoes()
  const { data: papeis = [] } = usePapeisDisponiveis()
  const updateMutation = useAtualizarPermissoesMenu()

  const [selecaoPorMenu, setSelecaoPorMenu] = useState<Record<number, number[]>>({})

  const selecaoParaMenu = (menu: MenuParaPermissao): number[] =>
    selecaoPorMenu[menu.codigo] ?? menu.papeisComAcesso.map((p) => p.codigo)

  const togglePapel = (menuCodigo: number, papelCodigo: number) => {
    setSelecaoPorMenu((prev) => {
      const atual = prev[menuCodigo] ?? menus.find((m) => m.codigo === menuCodigo)?.papeisComAcesso.map((p) => p.codigo) ?? []
      const set = new Set(atual)
      if (set.has(papelCodigo)) set.delete(papelCodigo)
      else set.add(papelCodigo)
      return { ...prev, [menuCodigo]: Array.from(set) }
    })
  }

  const handleSalvar = async (menu: MenuParaPermissao) => {
    const codigos = selecaoParaMenu(menu)
    await updateMutation.mutateAsync({ codigo: menu.codigo, papelCodigos: codigos })
    setSelecaoPorMenu((prev) => {
      const next = { ...prev }
      delete next[menu.codigo]
      return next
    })
  }

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-8 h-8 text-brand-500" />
            Permissões por menu
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Defina quais papéis podem ver cada item de menu. Apenas itens com Recurso definido aparecem abaixo.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-4 sm:mt-0 inline-flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-theme-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-gray-500">Carregando...</div>
        ) : menus.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            Nenhum item de menu com Recurso definido. Cadastre menus com o campo Recurso preenchido em Administração de Menus.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Menu
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Rota
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Recurso
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Papéis com acesso
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Ação
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {menus.map((menu) => (
                  <LinhaPermissao
                    key={menu.codigo}
                    menu={menu}
                    papeis={papeis}
                    papelCodigosSelecionados={selecaoParaMenu(menu)}
                    onTogglePapel={(codigo) => togglePapel(menu.codigo, codigo)}
                    onSalvar={() => handleSalvar(menu)}
                    salvando={updateMutation.isLoading}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default Permissoes
